"use client";

import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import { BADGE_CONFIG } from "../../_components/brief-card";
import { useBriefRowTracking } from "../../_components/use-brief-analytics";
import type { ExploreBriefCard } from "../../types";

function RelatedBriefRow({
  brief,
  positionInList,
  onOpen,
}: {
  brief: ExploreBriefCard;
  positionInList: number;
  onOpen: (brief: ExploreBriefCard) => void;
}) {
  const badge = BADGE_CONFIG[brief.briefType];
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface: "story_detail_related_briefs",
    positionInList,
    onClick: () => onOpen(brief),
  });
  return (
    <button
      className="block w-full cursor-pointer rounded-2xl bg-[var(--explore-bg-highlight)] px-4 py-3 text-left transition-colors hover:bg-[var(--explore-tag-bg)]"
      onClick={handleClick}
      ref={ref}
      type="button"
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className={`rounded-full px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>
      <div className="font-medium text-[0.92rem] text-[var(--explore-text-primary)] leading-[1.4]">
        {brief.title}
      </div>
    </button>
  );
}

const LazyBriefDetailDialog = dynamic(
  async () => {
    const mod = await import("../../_components/brief-detail-dialog");
    return mod.BriefDetailDialog;
  },
  { ssr: false }
);

export function ProductRelatedBriefs({
  briefs,
  exploreBase,
}: {
  briefs: ExploreBriefCard[];
  exploreBase: string;
}) {
  const [selectedBrief, setSelectedBrief] = useState<ExploreBriefCard | null>(
    null
  );
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback((brief: ExploreBriefCard) => {
    setSelectedBrief(brief);
    setOpen(true);
  }, []);

  if (briefs.length === 0) {
    return null;
  }

  return (
    <section className="rounded-[28px] border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-6 shadow-[var(--explore-shadow-card)]">
      <h2 className="mb-3 font-bold text-[1.05rem] text-[var(--explore-text-primary)]">
        Related briefs
      </h2>
      <div className="space-y-2">
        {briefs.map((brief, idx) => (
          <RelatedBriefRow
            brief={brief}
            key={brief.briefId}
            onOpen={handleOpen}
            positionInList={idx}
          />
        ))}
      </div>

      {selectedBrief ? (
        <LazyBriefDetailDialog
          brief={selectedBrief}
          exploreBase={exploreBase}
          onOpenChange={setOpen}
          open={open}
          surface="story_detail_related_briefs"
        />
      ) : null}
    </section>
  );
}
